import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import classNames from 'classnames';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import { useSelector } from 'react-redux';
import { toastMessage } from 'shared/components/toast/Toast';
import { BaseURL } from 'shared/utils/Endpoints';

export const getPublicRedirectOrigin = () => {
    if (BaseURL.includes('localhost') || BaseURL.includes('127.0.0.1')) {
        return 'https://api.wisetechshop.com';
    }
    return BaseURL.replace(/\/api\/?$/, '');
};

export default function ViewQRCodeModal({ show, handleClose, selectedItem }) {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);
    const [downloading, setDownloading] = useState(false);

    if (!selectedItem) return null;

    const isStatic = selectedItem.qr_type === 'static';
    const currentOrigin = getPublicRedirectOrigin();
    const dynamicRedirectUrl = `${currentOrigin}/qr/${selectedItem.code}`;

    let directTargetUrl = selectedItem.target_url;
    if (!directTargetUrl.startsWith('http://') && !directTargetUrl.startsWith('https://')) {
        directTargetUrl = 'https://' + directTargetUrl;
    }

    // Encoded URL in the QR image
    const encodedUrl = isStatic ? directTargetUrl : dynamicRedirectUrl;

    // Standard high-quality QR code image generator API
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(
        encodedUrl
    )}&margin=10`;

    const handleCopy = () => {
        navigator.clipboard.writeText(encodedUrl);
        toastMessage('success', `${isStatic ? 'Target' : 'Redirect'} URL copied to clipboard!`);
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await fetch(qrImageUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `QR_${selectedItem.code || 'code'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toastMessage('success', 'QR Code downloaded successfully!');
        } catch (error) {
            console.error(error);
            toastMessage('error', 'Failed to download QR code image.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="md" centered>
            <Modal.Header className={styles.Header}>
                <Heading heading={`QR Code: ${selectedItem.title}`} headingStyle={styles.AddLinkHeading} />
            </Modal.Header>
            <Modal.Body className={classNames(`${theme}ModalBackground`, styles.Body)}>
                <div className={styles.QRContainer}>
                    <div className="mb-2">
                        <span className={classNames('badge px-3 py-2', isStatic ? 'bg-success' : 'bg-primary')}>
                            {isStatic ? '🎯 Static QR (Direct Target URL)' : '⚡ Dynamic QR (Smart Editable Link)'}
                        </span>
                    </div>

                    <div className={styles.QRImageWrapper}>
                        <img
                            src={qrImageUrl}
                            alt={`QR for ${selectedItem.title}`}
                            className={styles.QRImage}
                            crossOrigin="anonymous"
                        />
                    </div>

                    <div className="text-center mb-3">
                        <div className="fw-bold fs-6 mb-1">{selectedItem.title}</div>
                        <div className="text-muted small">
                            {!isStatic && (
                                <>
                                    Status:{' '}
                                    <span className={classNames('badge', selectedItem.is_active ? 'bg-success' : 'bg-danger')}>
                                        {selectedItem.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="ms-2">Scans: <strong>{selectedItem.scan_count || 0}</strong></span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={styles.UrlBox}>
                        <div className="text-muted small mb-1 fw-bold">
                            {isStatic ? 'Target Link (Shows directly on scan):' : 'QR Scan Permanent URL:'}
                        </div>
                        <a href={encodedUrl} target="_blank" rel="noreferrer" className="text-info text-decoration-none fw-bold">
                            {encodedUrl}
                        </a>

                        {!isStatic && (
                            <>
                                <div className="text-muted small mt-2 mb-1 fw-bold">Current Target Destination:</div>
                                <a
                                    href={directTargetUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-secondary text-decoration-none"
                                >
                                    {selectedItem.target_url}
                                </a>
                            </>
                        )}
                    </div>

                    <div className={styles.ActionBtns}>
                        <button className={classNames(styles.ActionBtn, styles.CopyBtn)} onClick={handleCopy}>
                            📋 Copy Link
                        </button>
                        <button
                            className={classNames(styles.ActionBtn, styles.DownloadBtn)}
                            onClick={handleDownload}
                            disabled={downloading}
                        >
                            ⬇ {downloading ? 'Downloading...' : 'Download PNG'}
                        </button>
                    </div>

                    <div className="d-flex justify-content-end w-100 mt-4">
                        <CustomButton text={'Close'} containerStyle={styles.CancelBtn} submitHandle={handleClose} />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}
